import type { EnvironmentReading, TaskPlanProposal } from "@calcplan/ai-capabilities";
import {
  describeComponentry,
  FAMILY_LABELS,
  type ComponentryFamily,
  type ElementComponentry,
} from "./ai-element-componentry";

/**
 * O pedido descreve um móvel pelo que ele TEM ("móvel de 3 portas", "armário
 * com gavetas"); o plano aponta um elemento por id. Esta conferência compara
 * as duas coisas e registra — nunca bloqueia — quando não batem.
 *
 * Rodada de 21/08 11:24: "cuba em cima do móvel de 3 portas" virou cuba sobre
 * o "Balcão Gaveteiro" (4 gavetas, 0 portas), e o revisor de prancheta e o
 * crítico validaram a referência que o PLANO escolheu, não a que o PEDIDO
 * descreveu. A contradição estava numa única frase do plano. Aqui ela vira
 * objeção pendente nomeada, para o executor, o revisor e o crítico.
 */
export interface TargetIdentityFinding {
  severity: "objection" | "note";
  elementId: string;
  message: string;
}

interface Descriptor {
  family: ComponentryFamily;
  count: number | null;
  phrase: string;
}

const NUMBER_WORDS: Record<string, number> = {
  um: 1, uma: 1, dois: 2, duas: 2, tres: 3, quatro: 4, cinco: 5, seis: 6, sete: 7, oito: 8, nove: 9, dez: 10,
};

const FAMILY_WORDS: ReadonlyArray<readonly [ComponentryFamily, ReadonlyArray<string>]> = [
  ["portas", ["porta", "portas"]],
  ["gavetas", ["gaveta", "gavetas", "gavetao", "gavetoes"]],
  ["basculantes", ["basculante", "basculantes"]],
  ["prateleiras", ["prateleira", "prateleiras"]],
  ["nichos", ["nicho", "nichos"]],
];

function normalize(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("pt-BR");
}

function familyOfWord(word: string): ComponentryFamily | null {
  for (const [family, words] of FAMILY_WORDS) if (words.includes(word)) return family;
  return null;
}

/**
 * Descritores de conteúdo no pedido: "de 3 portas", "com gavetas", "duas
 * gavetas". A família sozinha não basta — "perto da porta" fala do vão do
 * cômodo, não de um móvel —, por isso o padrão exige número ou o "de/com" do
 * móvel descrito.
 */
export function contentDescriptors(objective: string): Descriptor[] {
  const words = normalize(objective).replace(/[^a-z0-9\s]+/g, " ").split(/\s+/).filter(Boolean);
  const found: Descriptor[] = [];
  const seen = new Set<string>();
  for (let index = 0; index < words.length; index += 1) {
    const family = familyOfWord(words[index]!);
    if (!family) continue;
    const previous = words[index - 1] ?? "";
    const beforePrevious = words[index - 2] ?? "";
    let count: number | null = null;
    let anchored = false;
    if (/^\d+$/.test(previous)) {
      count = Number(previous);
      anchored = true;
    } else if (previous in NUMBER_WORDS) {
      count = NUMBER_WORDS[previous]!;
      anchored = true;
    } else if (previous === "de" || previous === "com") {
      anchored = true;
    }
    if (count !== null && (beforePrevious === "de" || beforePrevious === "com")) anchored = true;
    if (!anchored) continue;
    const phrase = [count !== null ? previous : null, words[index]].filter(Boolean).join(" ");
    const key = `${family}:${count ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    found.push({ family, count, phrase });
  }
  return found;
}

interface ReadingElementLike {
  elementId: string;
  name: string;
  componentry?: ElementComponentry | null;
}

function findReadingElement(
  reading: Pick<EnvironmentReading, "elements">,
  id: string | null | undefined,
  name: string | null | undefined,
): ReadingElementLike | null {
  const elements = reading.elements as ReadingElementLike[];
  if (id) {
    const byId = elements.find((candidate) => candidate.elementId === id);
    if (byId) return byId;
  }
  if (name) {
    const target = normalize(name).trim();
    const matches = elements.filter((candidate) => normalize(candidate.name).trim() === target);
    if (matches.length === 1) return matches[0]!;
  }
  return null;
}

/**
 * Para cada decisão espacial, compara o que o pedido descreve com o que a
 * referência (e o sujeito, quando já existe) registra na árvore de componentes.
 * Objeção quando a árvore contradiz; nota quando não há árvore e só a foto
 * pode confirmar. Máximo de três achados, objeções primeiro.
 */
export function targetIdentityFindings(input: {
  objective: string;
  proposal: Pick<TaskPlanProposal, "spatialDecisions">;
  reading: Pick<EnvironmentReading, "elements"> | null | undefined;
}): TargetIdentityFinding[] {
  if (!input.reading) return [];
  const descriptors = contentDescriptors(input.objective);
  if (!descriptors.length) return [];
  const findings: TargetIdentityFinding[] = [];
  const visited = new Set<string>();
  for (const decision of input.proposal.spatialDecisions) {
    const bound: Array<{ role: string; element: ReadingElementLike | null }> = [
      { role: "referência", element: findReadingElement(input.reading, decision.referenceElementId, decision.reference) },
      { role: "sujeito", element: findReadingElement(input.reading, decision.subjectElementId, null) },
    ];
    for (const { role, element } of bound) {
      if (!element || visited.has(element.elementId)) continue;
      visited.add(element.elementId);
      const componentry = element.componentry ?? null;
      for (const descriptor of descriptors) {
        const label = FAMILY_LABELS[descriptor.family];
        const where = `a ${role} «${element.name}» da decisão de «${decision.subject}»`;
        if (!componentry) {
          findings.push({
            severity: "note",
            elementId: element.elementId,
            message: `Identidade do alvo: o pedido descreve «${descriptor.phrase}» e ${where} não tem árvore de componentes registrada — confirme pela foto que é este o móvel descrito antes de executar.`,
          });
          break;
        }
        // A árvore nomeia o que o autor do SKP nomeou: o gaveteiro real tem
        // quatro gavetas e três nós "Gaveta copy"; a quarta é um Component
        // anônimo. Família ausente é sinal forte (objeção); número diferente
        // é sinal fraco (nota) — em ambos, a foto decide.
        const count = componentry[descriptor.family] ?? 0;
        if (count === 0) {
          findings.push({
            severity: "objection",
            elementId: element.elementId,
            message: `Identidade do alvo: o pedido descreve «${descriptor.phrase}»; ${where} registra ${describeComponentry(componentry)} e nenhuma ${label.singular} nomeada na árvore de componentes — confirme o alvo pela foto ou escolha o móvel descrito.`,
          });
        } else if (descriptor.count !== null && count !== descriptor.count) {
          findings.push({
            severity: "note",
            elementId: element.elementId,
            message: `Identidade do alvo: o pedido descreve «${descriptor.phrase}»; ${where} registra ${count} ${count === 1 ? label.singular : label.plural} nomeada(s), não ${descriptor.count} — a árvore pode não nomear todas; confirme pela foto.`,
          });
        }
      }
    }
  }
  findings.sort((left, right) => (left.severity === right.severity ? 0 : left.severity === "objection" ? -1 : 1));
  return findings.slice(0, 3);
}
